export type AnalyticsProperties = Record<string, unknown>;

export interface AnalyticsProvider {
  capture: (event: string, properties?: AnalyticsProperties) => void | Promise<void>;
  identify: (distinctId: string, properties?: AnalyticsProperties) => void | Promise<void>;
  screen?: (screenName: string, properties?: AnalyticsProperties) => void | Promise<void>;
  captureException?: (error: unknown, properties?: AnalyticsProperties) => void | Promise<void>;
  reset?: () => void | Promise<void>;
}

type AnalyticsContextProvider = () => AnalyticsProperties | Promise<AnalyticsProperties>;

const analyticsContextProviders: AnalyticsContextProvider[] = [];

let analyticsProvider: AnalyticsProvider | null = null;
let defaultContextRegistered = false;

const buildEventProperties = async (properties: AnalyticsProperties = {}): Promise<AnalyticsProperties> => {
  const contextEntries = await Promise.all(
    analyticsContextProviders.map(async (provider) => {
      try {
        return await provider();
      } catch {
        return {};
      }
    }),
  );

  return Object.assign({}, ...contextEntries, properties);
};

export const registerAnalyticsProvider = (provider: AnalyticsProvider) => {
  analyticsProvider = provider;
};

export const registerAnalyticsContextProvider = (provider: AnalyticsContextProvider) => {
  analyticsContextProviders.push(provider);
};

export const registerDefaultAnalyticsContext = () => {
  if (defaultContextRegistered) {
    return;
  }

  registerAnalyticsContextProvider(() => {
    if (globalThis.window === undefined) {
      return {};
    }

    return {
      pathname: globalThis.window.location.pathname,
    };
  });

  defaultContextRegistered = true;
};

export const captureAnalyticsEvent = async (event: string, properties: AnalyticsProperties = {}) => {
  if (!analyticsProvider) {
    return;
  }

  await analyticsProvider.capture(event, await buildEventProperties(properties));
};

export const identifyAnalyticsUser = (distinctId: string, properties: AnalyticsProperties = {}) => {
  if (!analyticsProvider) {
    return;
  }

  return analyticsProvider.identify(distinctId, properties);
};

export const trackAnalyticsScreen = async (screenName: string, properties: AnalyticsProperties = {}) => {
  if (!analyticsProvider?.screen) {
    return;
  }

  await analyticsProvider.screen(screenName, await buildEventProperties(properties));
};

export const captureAnalyticsException = async (error: unknown, properties: AnalyticsProperties = {}) => {
  if (!analyticsProvider?.captureException) {
    return;
  }

  await analyticsProvider.captureException(error, await buildEventProperties(properties));
};

export const resetAnalytics = async () => {
  if (!analyticsProvider?.reset) {
    return;
  }

  await analyticsProvider.reset();
};
